import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, DateRangePicker, Form, Input, InputNumber, Message, SelectPicker } from 'rsuite';
import { Button as NsiButton, ConfirmModal, Modal, useLastPresent } from '@nsi-advisory-services/components';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { showToast } from '../utils/toast';
import {
  useDeleteTimesheetTask,
  useProPlanProjects,
  useSaveProPlanLink,
  useSaveTimesheetSettings,
  useSaveTimesheetTask,
  useTimesheetAdminAction,
  useTimesheetConfig,
  useTimesheetTasks,
} from '../hooks/useTimesheet';
import { Banner, ProviderBanner, Switch, TimesheetNav } from '../components/timesheet/TimesheetParts';
import RulesTab from '../components/timesheet/RulesAdmin';
import { dateFromIso, formatHours, formatTimestamp, isoFromDate, periodFor, previousPeriod, todayIso } from '../utils/timesheet';
import './Timesheet.css';

const TABS = [
  { key: 'projects', label: 'Projects & Tasks', icon: 'bi-diagram-3' },
  { key: 'rules', label: 'Rules', icon: 'bi-check2-square' },
  { key: 'settings', label: 'Settings', icon: 'bi-sliders' },
  { key: 'data', label: 'Import & Sync', icon: 'bi-arrow-left-right' },
];

const PROVIDER_HELP = {
  timecamp: 'Payroll, R&D credits, View Hours and Hours Breakdown read hours from TimeCamp. The EmployeeConnect timesheet can be set up and piloted alongside.',
  employeeconnect: 'Every hours consumer reads from this timesheet, and payroll and the validation rules carry on working against it.',
};

const PROJECT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

const blankTask = (parentId = null) => ({
  id: null,
  parentId,
  name: '',
  code: '',
  clientName: '',
  description: '',
  color: null,
  billable: false,
  // null follows the parent; for a project, null is off.
  rdEligible: null,
  budgetHours: null,
  archived: false,
  systemRole: null,
});

const proPlanProjectLabel = (p) => [p.projectNumber, p.name].filter(Boolean).join(' · ') || `ProPlan project ${p.id}`;
const proPlanTaskOrderLabel = (t) => [t.number, t.name].filter(Boolean).join(' · ') || `Task order ${t.id}`;

/*
  The ProPlanConnect link for a saved project or task, applied straight away
  (like archiving) rather than with Save. A project picks a ProPlan project;
  a task picks one of the task orders of the ProPlan project its top-level
  project is linked to. Each can be linked to only one, which is why options
  already linked elsewhere are disabled.
*/
function ProPlanLinkSection({ task, tasks, proplan }) {
  const linkMutation = useSaveProPlanLink();
  const [error, setError] = useState(null);
  const isProject = !task.parentId;
  const projects = proplan.data?.projects || [];
  const root = isProject ? task : tasks.find((t) => t.id === task.rootId);
  const rootProject = root?.proplanProjectId != null ? projects.find((p) => p.id === root.proplanProjectId) : null;
  const current = isProject ? task.proplanProjectId : task.proplanTaskOrderId;

  const save = async (value) => {
    setError(null);
    try {
      const result = await linkMutation.mutateAsync(isProject
        ? { id: task.id, proplanProjectId: value }
        : { id: task.id, proplanTaskOrderId: value });
      const cleared = result?.clearedTaskOrders ? ` ${result.clearedTaskOrders} task order link(s) under it were cleared.` : '';
      showToast('success', `${value == null ? 'Unlinked from ProPlan.' : 'Linked to ProPlan.'}${cleared}`);
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  let options = [];
  if (isProject) {
    options = projects.map((p) => {
      const elsewhere = p.linkedTask && p.linkedTask.id !== task.id;
      return {
        value: p.id,
        label: `${proPlanProjectLabel(p)}${elsewhere ? ` (linked to ${p.linkedTask.path})` : ''}`,
        client: p.client || 'No client',
        disabled: Boolean(elsewhere),
      };
    });
  } else if (rootProject) {
    options = rootProject.taskOrders.map((t) => {
      const elsewhere = t.linkedTask && t.linkedTask.id !== task.id;
      return {
        value: t.id,
        label: `${proPlanTaskOrderLabel(t)}${elsewhere ? ` (linked to ${t.linkedTask.path})` : ''}`,
        disabled: Boolean(elsewhere),
      };
    });
  }
  // Keep the saved link showing even when ProPlan is not answering.
  if (current != null && !options.some((o) => o.value === current)) {
    options = [{ value: current, label: `${isProject ? 'ProPlan project' : 'Task order'} ${current}`, client: '' }, ...options];
  }

  let help;
  if (proplan.data?.configured === false) help = proplan.data.message;
  else if (proplan.error) help = `ProPlanConnect could not be reached: ${String(proplan.error?.message || proplan.error)}`;
  else if (isProject) help = 'Projected revenue in reports uses the ProPlan rates of the linked project. Unlinking also clears the task order links under it.';
  else if (root?.proplanProjectId == null) help = `Link the project "${root?.name || ''}" to a ProPlan project first.`;
  else help = 'Optional. Only the task orders of the linked ProPlan project are offered.';

  const pickerDisabled = !isProject && root?.proplanProjectId == null;

  return (
    <section className="ts-taskmodal__manage" aria-label="ProPlanConnect link">
      <div className="ts-taskmodal__manage-title">ProPlan {isProject ? 'project' : 'task order'}</div>
      {error ? <Message type="error" showIcon className="mb-2">{error}</Message> : null}
      <div className="ts-taskmodal__manage-actions">
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <SelectPicker
            data={options}
            groupBy={isProject ? 'client' : undefined}
            value={current ?? null}
            onChange={(value) => (value != null && value !== current ? save(value) : null)}
            placeholder={isProject ? 'Not linked — pick a ProPlan project' : 'Not linked — pick a task order'}
            cleanable={false}
            loading={proplan.isLoading}
            disabled={pickerDisabled || linkMutation.isPending}
            block
            preventOverflow
          />
        </div>
        {current != null ? (
          <Button size="sm" appearance="ghost" onClick={() => save(null)} loading={linkMutation.isPending}>
            <i className="bi bi-x-lg me-1" aria-hidden="true" />
            Unlink
          </Button>
        ) : null}
      </div>
      <p className="ts-taskmodal__manage-help">{help}</p>
    </section>
  );
}

// Add or edit one project or task. An existing one can also be archived (or
// restored) and have a task added under it from the footer; deleting is only
// for something nobody has logged time to.
function TaskEditModal({ draft: liveDraft, setDraft, tasks, systemRoles, proplan, onClose, onAddUnder }) {
  // Always mounted; open while there is a draft, and showing the last one while it fades out.
  const draft = useLastPresent(liveDraft);
  const saveMutation = useSaveTimesheetTask();
  const deleteMutation = useDeleteTimesheetTask();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setConfirmDelete(false);
    setError(null);
  }, [draft?.id]);

  if (!draft) return null;
  const isProject = !draft.parentId;
  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  // R&D credit: a task that has not said shows (and keeps) its parent's
  // answer until somebody flips it; a project that has not said is off.
  const parentTask = draft.parentId ? tasks.find((t) => t.id === draft.parentId) : null;
  const rdShown = draft.rdEligible ?? (isProject ? false : Boolean(parentTask?.effectiveRdEligible));

  // A node cannot be moved under itself or its own descendants.
  const descendants = new Set();
  if (draft.id) {
    const pending = [draft.id];
    while (pending.length) {
      const parentId = pending.pop();
      tasks.forEach((t) => {
        if (t.parentId === parentId && !descendants.has(t.id)) {
          descendants.add(t.id);
          pending.push(t.id);
        }
      });
    }
  }
  const parentOptions = tasks
    .filter((t) => t.id !== draft.id && !descendants.has(t.id))
    .map((t) => ({ value: t.id, label: t.path, project: t.pathNames[0] }));
  const roleOptions = systemRoles.map((r) => ({ value: r.key, label: r.label, help: r.help }));

  const save = async () => {
    setError(null);
    try {
      await saveMutation.mutateAsync({
        id: draft.id || undefined,
        parentId: draft.parentId || null,
        name: draft.name,
        code: draft.code,
        clientName: isProject ? draft.clientName : null,
        description: draft.description,
        color: draft.color,
        billable: draft.billable,
        rdEligible: draft.rdEligible,
        budgetHours: draft.budgetHours === '' ? null : draft.budgetHours,
        archived: draft.archived,
        systemRole: draft.systemRole || null,
      });
      showToast('success', draft.id ? 'Saved.' : `${isProject ? 'Project' : 'Task'} created.`);
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  // Saved on its own, straight away: archiving is reversible, and the rest of
  // the form may hold edits the person has not decided on.
  const toggleArchive = async () => {
    setError(null);
    try {
      await saveMutation.mutateAsync({ id: draft.id, archived: !draft.archived });
      showToast('success', draft.archived ? `Restored ${draft.name}.` : `Archived ${draft.name}.`);
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  const remove = async () => {
    try {
      await deleteMutation.mutateAsync(draft.id);
      showToast('success', 'Deleted.');
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  return (
    <Modal open={Boolean(liveDraft)} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>{draft.id ? `Edit ${isProject ? 'Project' : 'Task'}` : `New ${isProject ? 'Project' : 'Task'}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <Message type="error" showIcon className="mb-3">{error}</Message> : null}
        <Form fluid>
          <Form.Group controlId="ts_task_name">
            <Form.ControlLabel>Name</Form.ControlLabel>
            <Input value={draft.name} onChange={(name) => set({ name })} autoFocus />
          </Form.Group>
          <Form.Group controlId="ts_task_parent">
            <Form.ControlLabel>Belongs to</Form.ControlLabel>
            <SelectPicker
              data={parentOptions}
              groupBy="project"
              value={draft.parentId}
              onChange={(parentId) => set({ parentId: parentId || null })}
              placeholder="Nothing (this is a project)"
              block
              preventOverflow
            />
          </Form.Group>
          <div className="d-flex gap-3 flex-wrap">
            <Form.Group controlId="ts_task_code" style={{ flex: '1 1 140px' }}>
              <Form.ControlLabel>Code</Form.ControlLabel>
              <Input value={draft.code || ''} onChange={(code) => set({ code })} placeholder="e.g. 24-117" />
            </Form.Group>
            {isProject ? (
              <Form.Group controlId="ts_task_client" style={{ flex: '2 1 220px' }}>
                <Form.ControlLabel>Client</Form.ControlLabel>
                <Input value={draft.clientName || ''} onChange={(clientName) => set({ clientName })} />
              </Form.Group>
            ) : null}
            <Form.Group controlId="ts_task_budget" style={{ flex: '1 1 140px' }}>
              <Form.ControlLabel>Budget hours</Form.ControlLabel>
              <InputNumber
                value={draft.budgetHours ?? ''}
                onChange={(value) => set({ budgetHours: value === '' || value === null ? null : Number(value) })}
                min={0}
                step={1}
              />
            </Form.Group>
          </div>
          <Form.Group controlId="ts_task_description">
            <Form.ControlLabel>Description</Form.ControlLabel>
            <Input as="textarea" rows={2} value={draft.description || ''} onChange={(description) => set({ description })} />
          </Form.Group>
          {isProject ? (
            <Form.Group controlId="ts_task_color">
              <Form.ControlLabel>Color</Form.ControlLabel>
              <div className="d-flex gap-2 flex-wrap">
                {[null, ...PROJECT_COLORS].map((c) => (
                  <button
                    key={c || 'none'}
                    type="button"
                    onClick={() => set({ color: c })}
                    aria-label={c ? `Color ${c}` : 'No color'}
                    aria-pressed={draft.color === c}
                    className="bdc-iconbtn"
                    style={{ boxShadow: draft.color === c ? '0 0 0 2px var(--bdc-primary)' : undefined }}
                  >
                    {c ? <span className="ts-color-swatch" style={{ background: c, width: 14, height: 14 }} /> : <i className="bi bi-slash-circle" aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </Form.Group>
          ) : null}
          <div className="d-flex gap-4 flex-wrap mb-3">
            <Switch checked={draft.billable} onChange={(billable) => set({ billable })} label="Billable" />
            <Switch checked={rdShown} onChange={(on) => set({ rdEligible: on })} label="R&D credit eligible" />
            {draft.parentId ? (
              <NsiButton
                size="sm"
                disabled={draft.rdEligible === null}
                onClick={() => set({ rdEligible: null })}
              >
                Use inherited setting
              </NsiButton>
            ) : null}
          </div>
          <div className="d-flex gap-3 flex-wrap">
            <Form.Group controlId="ts_task_role" style={{ flex: '1 1 100%' }}>
              <Form.ControlLabel>Type</Form.ControlLabel>
              <SelectPicker
                data={roleOptions}
                value={draft.systemRole}
                onChange={(systemRole) => set({ systemRole: systemRole || null })}
                placeholder="Regular work"
                searchable={false}
                block
                preventOverflow
              />
              <Form.HelpText>{systemRoles.find((r) => r.key === draft.systemRole)?.help || 'Leave empty for regular work. A type links the task to the timesheet checks.'}</Form.HelpText>
            </Form.Group>
          </div>
        </Form>

        {draft.id && tasks.find((t) => t.id === draft.id) ? (
          <ProPlanLinkSection task={tasks.find((t) => t.id === draft.id)} tasks={tasks} proplan={proplan} />
        ) : null}

        {/* What can be done to this one, apart from editing it: kept out of
            the footer so none of it sits beside Save. */}
        {draft.id ? (
          <section className="ts-taskmodal__manage" aria-label={`Manage this ${isProject ? 'project' : 'task'}`}>
            <div className="ts-taskmodal__manage-title">This {isProject ? 'project' : 'task'}</div>
            <div className="ts-taskmodal__manage-actions">
              {!draft.archived ? (
                <NsiButton size="sm" variant="accent" icon="plus-lg" onClick={() => onAddUnder(draft.id)}>
                  Add task under this
                </NsiButton>
              ) : null}
              <NsiButton
                size="sm"
                icon={draft.archived ? 'arrow-counterclockwise' : 'archive'}
                onClick={toggleArchive}
                disabled={saveMutation.isPending}
              >
                {draft.archived ? 'Restore' : 'Archive'}
              </NsiButton>
              {confirmDelete ? (
                <NsiButton size="sm" variant="danger-solid" onClick={remove} loading={deleteMutation.isPending}>Confirm delete</NsiButton>
              ) : (
                <NsiButton size="sm" variant="danger" icon="trash3" onClick={() => setConfirmDelete(true)}>
                  Delete
                </NsiButton>
              )}
            </div>
            <p className="ts-taskmodal__manage-help">
              Archiving hides it from the pickers and keeps its history. Only something nobody has logged time to can
              be deleted.
            </p>
          </section>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={onClose}>Cancel</NsiButton>
        <NsiButton variant="primary" onClick={save} disabled={!draft.name.trim()} loading={saveMutation.isPending}>Save</NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

// Name, code, client, billable, R&D, hours, type.
const COLUMNS = 7;

function ProjectsTab({ config }) {
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [draft, setDraft] = useState(null);
  const tasksQuery = useTimesheetTasks({ includeArchived: true, usage: true });
  const proplan = useProPlanProjects({ enabled: Boolean(config?.permissions?.canManage) });
  const tasks = useMemo(() => tasksQuery.data?.tasks || [], [tasksQuery.data]);
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  // Badge titles: what each linked ProPlan id is, when ProPlan answered.
  const proplanNames = useMemo(() => {
    const projects = new Map();
    const taskOrders = new Map();
    (proplan.data?.projects || []).forEach((p) => {
      projects.set(p.id, proPlanProjectLabel(p));
      p.taskOrders.forEach((t) => taskOrders.set(t.id, proPlanTaskOrderLabel(t)));
    });
    return { projects, taskOrders };
  }, [proplan.data]);
  const childCount = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => { if (t.parentId) m.set(t.parentId, (m.get(t.parentId) || 0) + 1); });
    return m;
  }, [tasks]);

  // Hours roll up the tree, so a project shows everything logged beneath it.
  const rolledHours = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => {
      let cursor = t;
      const seen = new Set();
      while (cursor && !seen.has(cursor.id)) {
        seen.add(cursor.id);
        m.set(cursor.id, (m.get(cursor.id) || 0) + (t.usedHours || 0));
        cursor = cursor.parentId ? byId.get(cursor.parentId) : null;
      }
    });
    return m;
  }, [tasks, byId]);

  const term = search.trim().toLowerCase();
  const matchIds = useMemo(() => {
    if (!term) return null;
    const ids = new Set();
    tasks.forEach((t) => {
      if (`${t.path} ${t.code || ''} ${t.clientName || ''}`.toLowerCase().includes(term)) {
        let cursor = t;
        while (cursor) {
          ids.add(cursor.id);
          cursor = cursor.parentId ? byId.get(cursor.parentId) : null;
        }
      }
    });
    return ids;
  }, [term, tasks, byId]);

  const visible = tasks.filter((t) => {
    if (!showArchived && t.effectiveArchived) return false;
    if (matchIds && !matchIds.has(t.id)) return false;
    if (!matchIds) {
      // Hidden while any ancestor is collapsed.
      let cursor = t.parentId ? byId.get(t.parentId) : null;
      while (cursor) {
        if (collapsed.has(cursor.id)) return false;
        cursor = cursor.parentId ? byId.get(cursor.parentId) : null;
      }
    }
    return true;
  });

  /*
    The table's rows: the visible tasks in tree order, plus an "Add task here"
    row closing every open branch - after its last visible descendant, at its
    children's depth. Not while searching (it would land among results) and
    not in an archived branch, which takes no new work.
  */
  const rows = [];
  const openBranches = [];
  const closeBranches = (depth) => {
    while (openBranches.length && openBranches[openBranches.length - 1].depth >= depth) {
      rows.push({ addUnder: openBranches.pop() });
    }
  };
  visible.forEach((t) => {
    closeBranches(t.depth);
    rows.push({ task: t });
    if (!matchIds && childCount.get(t.id) && !collapsed.has(t.id) && !t.effectiveArchived) openBranches.push(t);
  });
  closeBranches(0);

  const projectCount = tasks.filter((t) => !t.parentId && !t.effectiveArchived).length;
  const roleLabel = Object.fromEntries((config?.systemRoles || []).map((r) => [r.key, r.label]));

  const openEdit = (t) => setDraft({
    id: t.id,
    parentId: t.parentId,
    name: t.name,
    code: t.code || '',
    clientName: t.clientName || '',
    description: t.description || '',
    color: t.color,
    billable: t.billable,
    rdEligible: t.rdEligible ?? null,
    budgetHours: t.budgetHours,
    archived: t.archived,
    systemRole: t.systemRole,
  });

  return (
    <>
      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">Projects</h3>
            <span className="bdc-count">{projectCount}</span>
          </div>
          <div className="bdc-table-head__actions">
            <div style={{ width: 240 }}>
              <Input value={search} onChange={setSearch} placeholder="Search name, code, client, ID" size="sm" />
            </div>
            <Switch checked={showArchived} onChange={setShowArchived} label="Show archived" />
            <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(tasks.filter((t) => childCount.get(t.id)).map((t) => t.id)))}>
              {collapsed.size ? 'Expand all' : 'Collapse all'}
            </button>
            <button type="button" className="bdc-btn bdc-btn--sm bdc-btn--primary" onClick={() => setDraft(blankTask())}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New project
            </button>
          </div>
        </div>
        {tasksQuery.error ? <Message type="error" showIcon>{String(tasksQuery.error?.message || tasksQuery.error)}</Message> : null}
        <table className="nsi-table">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ width: 110 }}>Code</th>
              <th style={{ width: 160 }}>Client</th>
              <th style={{ width: 90 }}>Billable</th>
              <th style={{ width: 110 }}>R&amp;D credit</th>
              <th style={{ width: 170 }} className="nsi-table__right">Hours / budget</th>
              <th style={{ width: 160 }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {tasksQuery.isLoading ? (
              <tr><td colSpan={COLUMNS} className="nsi-table__empty">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS} className="nsi-table__empty">
                  {tasks.length === 0
                    ? 'No projects yet. Create one, or import the task list from TimeCamp under Import & Sync.'
                    : 'Nothing matches.'}
                </td>
              </tr>
            ) : rows.map((row) => {
              if (row.addUnder) {
                return (
                  <tr key={`add:${row.addUnder.id}`} className="ts-tree-addrow">
                    <td colSpan={COLUMNS}>
                      <div className="ts-tree-name" style={{ paddingLeft: (row.addUnder.depth + 1) * 22 }}>
                        <span className="ts-tree-spacer" />
                        <button type="button" className="ts-tree-addbtn" onClick={() => setDraft(blankTask(row.addUnder.id))}>
                          <i className="bi bi-plus-lg" aria-hidden="true" />
                          Add task here
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }
              const t = row.task;
              const hasChildren = Boolean(childCount.get(t.id));
              const isCollapsed = collapsed.has(t.id) && !matchIds;
              const hours = rolledHours.get(t.id) || 0;
              const root = byId.get(t.rootId);
              const over = t.budgetHours != null && hours > t.budgetHours;
              return (
                <tr
                  key={t.id}
                  className={`ts-tree-row${t.effectiveArchived ? ' ts-tree-row--archived' : ''}`}
                  tabIndex={0}
                  onClick={() => openEdit(t)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      openEdit(t);
                    }
                  }}
                  aria-label={`Edit ${t.path}`}
                >
                  <td>
                    <div className="ts-tree-name" style={{ paddingLeft: t.depth * 22 }}>
                      {hasChildren ? (
                        <button
                          type="button"
                          className="ts-tree-toggle"
                          aria-expanded={!isCollapsed}
                          aria-label={isCollapsed ? `Expand ${t.name}` : `Collapse ${t.name}`}
                          // Opening a branch is not a request to edit it.
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollapsed((prev) => {
                              const next = new Set(prev);
                              if (next.has(t.id)) next.delete(t.id);
                              else next.add(t.id);
                              return next;
                            });
                          }}
                        >
                          <i className="bi bi-chevron-right" aria-hidden="true" />
                        </button>
                      ) : <span className="ts-tree-spacer" />}
                      {!t.parentId ? <span className="ts-color-swatch" style={{ background: t.color || root?.color || 'var(--bdc-border-strong)' }} aria-hidden="true" /> : null}
                      <span className={`ts-tree-label${t.parentId ? '' : ' ts-tree-label--project'}`} title={t.path}>{t.name}</span>
                      {t.archived ? <span className="ts-pill">Archived</span> : null}
                      {t.proplanProjectId != null || t.proplanTaskOrderId != null ? (
                        <span
                          className="ts-pill ts-pill--info"
                          title={t.proplanProjectId != null
                            ? `Linked to ProPlan project ${proplanNames.projects.get(t.proplanProjectId) || t.proplanProjectId}`
                            : `Linked to ProPlan task order ${proplanNames.taskOrders.get(t.proplanTaskOrderId) || t.proplanTaskOrderId}`}
                        >
                          <i className="bi bi-link-45deg" aria-hidden="true" />
                          ProPlan
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="nsi-table__muted">{t.code || '—'}</td>
                  <td className="nsi-table__muted">{t.clientName || (t.parentId ? '' : '—')}</td>
                  <td>{t.billable ? <span className="ts-billable"><i className="bi bi-check-lg" aria-hidden="true" />Yes</span> : <span className="nsi-table__muted">No</span>}</td>
                  <td>
                    {t.effectiveRdEligible ? <span className="ts-rdtag">R&amp;D</span> : <span className="nsi-table__muted">No</span>}
                    {t.parentId && t.rdEligible === null ? <span className="nsi-table__muted small ms-1">inherited</span> : null}
                  </td>
                  <td className={`nsi-table__right bdc-num${over ? ' bdc-text-bad' : ''}`}>
                    {formatHours(hours)}{t.budgetHours != null ? ` / ${formatHours(t.budgetHours)}` : ''}
                  </td>
                  <td>{t.systemRole ? <span className="ts-pill ts-pill--info">{roleLabel[t.systemRole] || t.systemRole}</span> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TaskEditModal
        draft={draft}
        setDraft={setDraft}
        tasks={tasks}
        systemRoles={config?.systemRoles || []}
        proplan={proplan}
        onClose={() => setDraft(null)}
        onAddUnder={(parentId) => setDraft(blankTask(parentId))}
      />
    </>
  );
}

function SettingsTab({ config }) {
  const saveMutation = useSaveTimesheetSettings();
  const tasksQuery = useTimesheetTasks({ includeArchived: false });
  const [pendingProvider, setPendingProvider] = useState(null);
  const settings = config?.settings || {};
  const tasks = tasksQuery.data?.tasks || [];

  const save = async (body, message = 'Settings saved.') => {
    try {
      await saveMutation.mutateAsync(body);
      showToast('success', message);
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  // What has to be true before EmployeeConnect can carry payroll.
  const roleHolders = (config?.systemRoles || []).map((r) => ({ ...r, task: tasks.find((t) => t.systemRole === r.key) }));
  const checks = [
    { ok: config?.installed, label: 'Database tables installed' },
    { ok: tasks.some((t) => !t.parentId), label: 'At least one project set up' },
    ...roleHolders.map((r) => ({
      ok: Boolean(r.task),
      label: r.task ? `${r.label}: ${r.task.path}` : `${r.label}: no task has this role yet`,
      warnOnly: r.key !== 'paid_time_off',
    })),
  ];

  return (
    <>
      <Section title="System of Record">
        <div className="ts-provider-options" role="radiogroup" aria-label="Time provider">
          {(config?.providers || []).map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={config.provider === p.key}
              className="ts-provider"
              onClick={() => (config.provider === p.key ? null : setPendingProvider(p.key))}
            >
              <span className="ts-provider__radio" aria-hidden="true" />
              <span>
                <span className="ts-provider__name d-block">{p.label}</span>
                <span className="ts-provider__help d-block">{PROVIDER_HELP[p.key]}</span>
              </span>
            </button>
          ))}
        </div>
        {!config?.timeCampConfigured ? (
          <p className="text-muted small mb-0">TIMECAMP_API_TOKEN is not set on the server, so TimeCamp cannot be read or imported from.</p>
        ) : null}
      </Section>

      <Section title="Readiness" note="for running payroll from EmployeeConnect">
        <ul className="ts-checklist">
          {checks.map((c) => (
            <li key={c.label}>
              <i
                className={`bi ${c.ok ? 'bi-check-circle-fill bdc-text-good' : c.warnOnly ? 'bi-exclamation-circle bdc-text-warn' : 'bi-x-circle-fill bdc-text-bad'}`}
                aria-label={c.ok ? 'Done' : 'Not done'}
              />
              <span>{c.label}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Logging">
        <div className="ts-settings">
          <div className="ts-setting">
            <div className="ts-setting__main">
              <div className="ts-setting__name">Timer rounding</div>
              <div className="ts-setting__help">
                A stopped timer's length is rounded to this step, and its start to the nearest step, so the times and
                the length agree. A very short timer can round down to nothing.
              </div>
            </div>
            <div className="ts-setting__control d-flex gap-2">
              <SelectPicker
                data={[
                  { value: 'nearest', label: 'To the nearest' },
                  { value: 'up', label: 'Always up' },
                  { value: 'down', label: 'Always down' },
                ]}
                value={settings.timerRoundingMode || 'nearest'}
                onChange={(value) => value && save({ timerRoundingMode: value })}
                cleanable={false}
                searchable={false}
                style={{ width: 160 }}
              />
              <SelectPicker
                data={[1, 5, 6, 10, 15, 30].map((m) => ({ value: m, label: m === 1 ? 'Minute' : `${m} minutes` }))}
                value={settings.timerRoundingMinutes}
                onChange={(value) => value && save({ timerRoundingMinutes: value })}
                cleanable={false}
                searchable={false}
                style={{ width: 140 }}
              />
            </div>
          </div>
          <div className="ts-setting">
            <div className="ts-setting__main">
              <div className="ts-setting__name">Require a description</div>
              <div className="ts-setting__help">Every entry needs a note of what was done, except breaks, time off and close dates.</div>
            </div>
            <div className="ts-setting__control">
              <Switch checked={settings.requireDescription} onChange={(v) => save({ requireDescription: v })} label={settings.requireDescription ? 'On' : 'Off'} />
            </div>
          </div>
          <div className="ts-setting">
            <div className="ts-setting__main">
              <div className="ts-setting__name">Log approved time off automatically</div>
              <div className="ts-setting__help">
                Reviewed-and-approved time-off requests are written to the Paid Time Off task, and follow the request if it
                changes. Entered (no review) time off is unpaid and is never logged.
              </div>
            </div>
            <div className="ts-setting__control">
              <Switch checked={settings.autoLogTimeOff} onChange={(v) => save({ autoLogTimeOff: v })} label={settings.autoLogTimeOff ? 'On' : 'Off'} />
            </div>
          </div>
          <div className="ts-setting">
            <div className="ts-setting__main">
              <div className="ts-setting__name">Allow overlapping entries</div>
              <div className="ts-setting__help">
                When off, an entry's start and end times can't cross another entry's on the same day. Entries logged as
                just a length are never checked, and a stopped timer that would overlap is saved without times.
              </div>
            </div>
            <div className="ts-setting__control">
              <Switch checked={settings.allowOverlaps} onChange={(v) => save({ allowOverlaps: v })} label={settings.allowOverlaps ? 'On' : 'Off'} />
            </div>
          </div>
        </div>
        {settings.updatedAt ? <p className="text-muted small mb-0">Last changed {formatTimestamp(settings.updatedAt)}.</p> : null}
      </Section>

      <ConfirmModal
        open={Boolean(pendingProvider)}
        title={`Switch to ${config.providers.find((p) => p.key === pendingProvider)?.label}?`}
        body={(
          <>
            <p>{PROVIDER_HELP[pendingProvider]}</p>
            <Message type="warning" showIcon>
              This changes where payroll, R&amp;D credits, View Hours and Hours Breakdown read hours from, for everyone,
              starting now. Switch between pay periods, after importing the period’s TimeCamp entries.
            </Message>
          </>
        )}
        confirmText="Switch"
        loading={saveMutation.isPending}
        onConfirm={async () => {
          await save({ provider: pendingProvider }, 'Time provider switched.');
          setPendingProvider(null);
        }}
        onCancel={() => setPendingProvider(null)}
      />
    </>
  );
}

function RangeAction({ title, description, children, range, setRange, onRun, running, result, runLabel, disabled = false }) {
  return (
    <div className="ts-setting align-items-start">
      <div className="ts-setting__main">
        <div className="ts-setting__name">{title}</div>
        <div className="ts-setting__help">{description}</div>
        {children}
        {result ? <div className="ts-result">{result}</div> : null}
      </div>
      <div className="ts-setting__control">
        {setRange ? (
          <div style={{ width: 240 }}>
            <DateRangePicker
              value={range}
              onChange={(value) => setRange(value)}
              format="MM/dd/yyyy"
              character=" – "
              isoWeek={false}
              weekStart={0}
              cleanable={false}
              placement="bottomEnd"
              block
            />
          </div>
        ) : null}
        <button type="button" className="bdc-btn bdc-btn--primary" onClick={onRun} disabled={running || disabled}>
          {running ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-play-fill" aria-hidden="true" />}
          {runLabel}
        </button>
      </div>
    </div>
  );
}

function DataTab({ config }) {
  const action = useTimesheetAdminAction();
  const lastPeriod = previousPeriod(periodFor(todayIso()));
  const [entryRange, setEntryRange] = useState([dateFromIso(lastPeriod.start), dateFromIso(lastPeriod.end)]);
  const [syncRange, setSyncRange] = useState([dateFromIso(`${todayIso().slice(0, 4)}-01-01`), dateFromIso(todayIso())]);
  const [skipTimeOff, setSkipTimeOff] = useState(true);
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});

  const run = async (key, body) => {
    setRunning(key);
    try {
      const result = await action.mutateAsync({ kind: key, ...body });
      setResults((prev) => ({ ...prev, [key]: result }));
      showToast('success', 'Done.');
    } catch (err) {
      setResults((prev) => ({ ...prev, [key]: { error: String(err?.message || err) } }));
      showToast('error', String(err?.message || err));
    } finally {
      setRunning(null);
    }
  };

  const describe = (key) => {
    const r = results[key];
    if (!r) return null;
    if (r.error) return <span className="bdc-text-bad">{r.error}</span>;
    if (key === 'import/catalog') {
      return `${r.total} TimeCamp tasks: ${r.created} created, ${r.updated} updated, ${r.rolesAssigned} types assigned${r.unplaced ? `, ${r.unplaced} could not be placed` : ''}.`;
    }
    if (key === 'import/entries') {
      return (
        <>
          {`${r.fetched} fetched, ${r.imported} imported. Skipped: ${r.skippedNoEmployee} with no matching employee, ${r.skippedNoTask} with no matching task, ${r.skippedTimeOff} paid time off.`}
          {r.unmatchedTaskIds?.length ? <div>Unmatched TimeCamp task IDs: {r.unmatchedTaskIds.join(', ')}</div> : null}
        </>
      );
    }
    return `${r.requests} requests checked: ${r.created} entries written, ${r.removed} removed${r.errors ? `, ${r.errors} errors` : ''}${r.skipped ? ` (stopped: ${r.skipped})` : ''}.`;
  };

  const toIsoRange = (range) => ({ start: isoFromDate(range[0]), end: isoFromDate(range[1]) });

  return (
    <>
      <Banner icon="bi-signpost-split" title="Moving off TimeCamp">
        1. Import the task catalog. 2. Give the special tasks their roles (Projects &amp; Tasks). 3. Import entries for
        the periods you need. 4. Sync approved time off. 5. Switch the system of record under Settings.
      </Banner>
      <Section title="TimeCamp">
        <div className="ts-settings">
          <RangeAction
            title="Import task catalog"
            description="Copies TimeCamp's project and task tree, archived tasks included, keyed on the TimeCamp task ID. Safe to re-run: new tasks are added and names, billable and archived follow TimeCamp."
            onRun={() => run('import/catalog', {})}
            running={running === 'import/catalog'}
            result={describe('import/catalog')}
            runLabel="Import"
            disabled={!config?.timeCampConfigured}
          />
          <RangeAction
            title="Import entries"
            description="Copies TimeCamp time entries for the range, matched to employees by TimeCamp user ID or email. Re-running updates entries rather than duplicating them. At most a year at a time."
            range={entryRange}
            setRange={setEntryRange}
            onRun={() => run('import/entries', { ...toIsoRange(entryRange), skipTimeOff })}
            running={running === 'import/entries'}
            result={describe('import/entries')}
            runLabel="Import"
            disabled={!config?.timeCampConfigured || !entryRange}
          >
            <div className="mt-2">
              <Switch
                checked={skipTimeOff}
                onChange={setSkipTimeOff}
                label="Skip paid time off (approved requests create their own entries)"
              />
            </div>
          </RangeAction>
        </div>
      </Section>
      <Section title="Time Off">
        <div className="ts-settings">
          <RangeAction
            title="Sync approved time off"
            description="Rebuilds Paid Time Off entries from time-off requests touching the range, which is useful after the Paid Time Off task is set up or the switch is made. Requests that are not reviewed and approved lose any entries."
            range={syncRange}
            setRange={setSyncRange}
            onRun={() => run('time-off-sync', toIsoRange(syncRange))}
            running={running === 'time-off-sync'}
            result={describe('time-off-sync')}
            runLabel="Sync"
            disabled={!syncRange}
          />
        </div>
      </Section>
    </>
  );
}

/**
 * Timesheet admin: the project/task tree, the settings (including which system
 * is the source of hours), and the tools for moving over from TimeCamp.
 */
export default function TimesheetAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'projects';
  const configQuery = useTimesheetConfig();
  const config = configQuery.data;

  return (
    <div className="bdc-page ts-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Configuration', to: '/admin/configuration' },
          { label: 'Timesheet' },
        ]}
        icon="bi-stopwatch"
        title="Timesheet"
        actions={<TimesheetNav config={config} />}
        showBack={false}
      />
      <div className="ts-nav">
        <div className="bdc-segment" role="tablist" aria-label="Timesheet settings sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`bdc-btn bdc-btn--sm${tab === t.key ? ' bdc-btn--primary' : ''}`}
              onClick={() => setSearchParams({ tab: t.key }, { replace: true })}
            >
              <i className={`bi ${t.icon}`} aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <ProviderBanner config={config} />
      {configQuery.error ? <Message type="error" showIcon>{String(configQuery.error?.message || configQuery.error)}</Message> : null}
      {config?.installed === false ? null : tab === 'projects' ? (
        <ProjectsTab config={config} />
      ) : tab === 'rules' ? (
        <RulesTab />
      ) : tab === 'settings' ? (
        <SettingsTab config={config} />
      ) : (
        <DataTab config={config} />
      )}
    </div>
  );
}
